import React, { useEffect, useRef } from "react";
import {
  FaCalendarCheck,
  FaCalendarTimes,
  FaCreditCard,
  FaEnvelope,
  FaEuroSign,
  FaInfoCircle,
  FaTag,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa";

import {
  formatCurrency,
  formatLongDateFr,
  getPaymentLabel,
  getPaymentStyle,
  getRenewalMessage,
  getRenewalStatusLabel,
  getRenewalStatusStyle,
  getSubscriptionAmount,
  getSubscriptionExpiryDate,
  getSubscriptionFormula,
  getSubscriptionGradient,
  getSubscriptionPurchaseDate,
  getSubscriptionUserEmail,
  getSubscriptionUserName,
} from "../utils/adminSubscriptionsUtils";

function InfoBox({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-sky-700 shadow-sm dark:bg-slate-950 dark:text-sky-200">
            <Icon />
          </div>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>

          <p className="mt-2 break-words text-sm font-bold text-slate-800 dark:text-white/80">
            {value || "Non renseigné"}
          </p>
        </div>
      </div>
    </div>
  );
}

function SubscriptionDetailsDrawer({ isOpen, subscription, onClose, onDelete }) {
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    const handleKeyDown = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !subscription) return null;

  const gradient = getSubscriptionGradient(subscription);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-slate-950/70 backdrop-blur-sm">
      <aside
        className="h-full w-full max-w-3xl bg-white shadow-2xl dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex h-full flex-col">
          <div className={`bg-gradient-to-br ${gradient} p-5 text-white`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[1.5rem] bg-white/15 text-2xl backdrop-blur-xl">
                  <FaCreditCard />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
                    Fiche abonnement
                  </p>

                  <h2 className="mt-2 font-titre text-2xl font-bold">
                    {getSubscriptionUserName(subscription)}
                  </h2>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="inline-flex min-h-8 items-center gap-2 rounded-2xl bg-white/15 px-3 text-xs font-bold text-white">
                      <FaTag />
                      {getSubscriptionFormula(subscription)}
                    </span>

                    <span
                      className={`inline-flex min-h-8 items-center rounded-2xl px-3 text-xs font-bold ${getRenewalStatusStyle(
                        subscription,
                      )}`}
                    >
                      {getRenewalStatusLabel(subscription)}
                    </span>
                  </div>
                </div>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur-xl transition hover:bg-white/25"
                aria-label="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <InfoBox icon={FaTag} label="ID achat" value={`#${subscription.id}`} />
              <InfoBox
                icon={FaUser}
                label="Client"
                value={getSubscriptionUserName(subscription)}
              />
              <InfoBox
                icon={FaEnvelope}
                label="Email"
                value={getSubscriptionUserEmail(subscription)}
              />
              <InfoBox
                icon={FaCreditCard}
                label="Formule"
                value={getSubscriptionFormula(subscription)}
              />
              <InfoBox
                icon={FaEuroSign}
                label="Montant"
                value={formatCurrency(getSubscriptionAmount(subscription))}
              />
              <InfoBox
                icon={FaInfoCircle}
                label="Paiement"
                value={getPaymentLabel(subscription)}
              />
              <InfoBox
                icon={FaCalendarCheck}
                label="Date d’achat"
                value={formatLongDateFr(getSubscriptionPurchaseDate(subscription))}
              />
              <InfoBox
                icon={FaCalendarTimes}
                label="Date d’échéance"
                value={formatLongDateFr(getSubscriptionExpiryDate(subscription))}
              />
            </div>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-2xl ${getRenewalStatusStyle(
                    subscription,
                  )}`}
                >
                  <FaCalendarTimes />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Suivi renouvellement
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {getRenewalStatusLabel(subscription)}
                  </h3>
                </div>
              </div>

              <p className="mt-4 text-sm font-medium leading-7 text-slate-600 dark:text-white/60">
                {getRenewalMessage(subscription)}
              </p>
            </section>

            <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-lg shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-2xl ${getPaymentStyle(
                    subscription,
                  )}`}
                >
                  <FaCreditCard />
                </div>

                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                    Statut règlement
                  </p>

                  <h3 className="font-titre text-xl font-bold text-slate-950 dark:text-white">
                    {getPaymentLabel(subscription)}
                  </h3>
                </div>
              </div>

              <p className="mt-4 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                Statut interne : {subscription.statut_reglement || "Non renseigné"}
                {subscription.payment_status
                  ? ` · Stripe : ${subscription.payment_status}`
                  : ""}
              </p>
            </section>
          </div>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-900">
            <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-blue-800"
              >
                <FaTimes />
                Fermer la fiche abonnement
              </button>

              <button
                type="button"
                onClick={() => onDelete(subscription.id)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-5 py-3 text-sm font-bold text-red-700 transition hover:-translate-y-0.5 hover:bg-red-100 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200"
              >
                <FaTrash />
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default SubscriptionDetailsDrawer;